// "The app came back to the foreground", on web and on native — CCT-476.
//
// ⚠️ `typeof window !== 'undefined'` IS NOT A WEB TEST, AND THAT COST A CRASH. React Native
// defines `window` as an alias of `globalThis`, so the guard passes on a phone and the very next
// line calls `window.addEventListener`, which does not exist there. Measured: every native
// sign-in died on `AuthContext.tsx:288` with "undefined is not a function", by OTP and by Google
// alike, because the effect re-arms on `user?.id` exactly when one becomes signed in.
//
// ⚠️ ONE HELPER RATHER THAN A PLATFORM TEST PER CALL SITE. The same wrong guard existed in two
// places; a third would have been written the same way. `beacon.ts` already had the right shape
// (AppState on native) and nobody reused it, which is the whole argument for naming it.
import { AppState, Platform } from 'react-native';

/**
 * Call `cb` whenever the app becomes visible again. Returns the unsubscribe function.
 *
 * On web that is the window regaining focus; on native it is AppState returning to `active`.
 * The two are not identical, but they answer the same question: "is the user looking at us
 * again, so is it worth re-checking something".
 */
export function onForeground(cb: () => void): () => void {
  if (Platform.OS === 'web') {
    // ⚠️ THE FUNCTION IS CHECKED, NOT THE OBJECT. A runtime that defines `window` without the
    // DOM would otherwise walk straight into the same crash this helper exists to prevent.
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return () => { /* nothing to unsubscribe */ };
    }
    window.addEventListener('focus', cb);
    return () => window.removeEventListener('focus', cb);
  }

  const sub = AppState.addEventListener('change', (state) => {
    if (state === 'active') cb();
  });
  // RN >= 0.65 returns a subscription; older versions returned nothing and needed `removeEventListener`.
  return () => {
    if (sub && typeof sub.remove === 'function') sub.remove();
  };
}
